// ============================================================================
// THE CONNECTIONS GLYPHS — transcribed path-for-path from
// docs/design/handoff/"LifeOS Connections.dc.html" (options 12a and 12b).
//
// Same idiom as components/icons.tsx: viewBox 0 0 24 24, fill none, stroke
// currentColor, round caps and joins, colour from a `text-*` token on the
// caller. They live here rather than in the shared set because every one of them
// appears on this screen and nowhere else in the handoff; the day a second
// screen needs one, it moves.
// ============================================================================

export interface GlyphProps {
  size?: number
  strokeWidth?: number
  className?: string
}

function Glyph({ size = 15, strokeWidth = 1.8, className, d }: GlyphProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

// ── Settings nav ────────────────────────────────────────────────────────────

/** Account. */
export const User = (p: GlyphProps) => (
  <Glyph {...p} d="M12 4.4a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2M4.8 20c.6-3.4 3.6-5.4 7.2-5.4s6.6 2 7.2 5.4" />
)

/** Connections — two links of a chain. */
export const Link = (p: GlyphProps) => (
  <Glyph {...p} d="M10.4 13.6 8 16a3.4 3.4 0 0 1-4.8-4.8l2.4-2.4M13.6 10.4 16 8a3.4 3.4 0 0 0-4.8-4.8L8.8 5.6M9.2 14.8l5.6-5.6" />
)

/** Privacy & data. */
export const Shield = (p: GlyphProps) => (
  <Glyph {...p} d="M12 3.6 4.6 6.4v5.2c0 4 3 7.4 7.4 8.8 4.4-1.4 7.4-4.8 7.4-8.8V6.4z" />
)

/** Journal defaults. */
export const Bookmark = (p: GlyphProps) => <Glyph {...p} d="M6 4.6h12v14.8l-6-3.4-6 3.4z" />

/** Notifications. */
export const Bulb = (p: GlyphProps) => (
  <Glyph {...p} d="M12 5.4a5.4 5.4 0 0 1 5.4 5.4c0 3-2.2 3.6-2.2 6H8.8c0-2.4-2.2-3-2.2-6A5.4 5.4 0 0 1 12 5.4M9.8 19.4h4.4" />
)

/** Appearance. */
export const Monitor = (p: GlyphProps) => <Glyph {...p} d="M4.6 6.6h14.8v10.8H4.6zM8.4 20h7.2" />

// ── Header and banner ───────────────────────────────────────────────────────

/** Sync all. */
export const Refresh = (p: GlyphProps) => (
  <Glyph strokeWidth={2} {...p} d="M19.4 12a7.4 7.4 0 1 1-2.2-5.2M19.4 4.6v4h-4" />
)

/** Add connector. */
export const Plus = (p: GlyphProps) => <Glyph strokeWidth={2.3} {...p} d="M12 6v12M6 12h12" />

/** The page-level consequence banner. */
export const AlertCircle = (p: GlyphProps) => (
  <Glyph strokeWidth={2} {...p} d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4M12 7.8v5M12 15.8h.01" />
)

// ── Connector marks ─────────────────────────────────────────────────────────
//
// PLACEHOLDERS, and the handoff says so (§Assets): the real Canvas / Reddit /
// Oura / Gmail marks are subject to each service's brand guidelines and are not
// in this bundle. Canvas is a monogram rather than a glyph, drawn by
// ConnectorLogo.

export const RedditFace = (p: GlyphProps) => (
  <Glyph {...p} d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6M8.6 11.4a1 1 0 1 0 0 .01M15.4 11.4a1 1 0 1 0 0 .01M9 15.2c1.8 1.2 4.2 1.2 6 0" />
)

export const OuraRing = (p: GlyphProps) => (
  <Glyph {...p} d="M12 4.6a7.4 7.4 0 1 1 0 14.8 7.4 7.4 0 0 1 0-14.8M12 8.8a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4" />
)

export const Envelope = (p: GlyphProps) => (
  <Glyph {...p} d="M3.6 6.8h16.8v10.4H3.6zM3.6 7.4 12 13l8.4-5.6" />
)

// ── Your machine, capture tokens, storage ───────────────────────────────────

/** The desktop worker. Wider than the Appearance monitor, and a different path. */
export const Desktop = (p: GlyphProps) => <Glyph {...p} d="M4 5.6h16v9.6H4zM2.6 18.4h18.8" />

export const Phone = (p: GlyphProps) => <Glyph {...p} d="M7.6 3.6h8.8v16.8H7.6zM10.8 18.2h2.4" />

/** The browser extension. */
export const BrowserPuzzle = (p: GlyphProps) => (
  <Glyph {...p} d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6M12 8.6a3.4 3.4 0 1 1 0 6.8 3.4 3.4 0 0 1 0-6.8M14.9 9.4h6M9.1 13.4l-3 5.2" />
)

export const Tablet = (p: GlyphProps) => <Glyph {...p} d="M4.6 5.4h14.8v13.2H4.6zM8.2 9h7.6" />

/** The storage note pinned at the bottom. */
export const Drive = (p: GlyphProps) => (
  <Glyph {...p} d="M4.6 7.4h14.8v9.2H4.6zM7.6 10.4h.01M7.6 13.6h.01" />
)
